/**
 * Hikvision Turbo HD DVR catalog — every SKU below is verified against a
 * primary Hikvision datasheet PDF hosted on assets.hikvision.com (the
 * official Hikvision CDN), or the matching distributor mirror of the
 * same Hikvision PDF when assets.hikvision.com gates the file behind
 * region restrictions.
 *
 * Every entry carries `hybrid: true` — Turbo HD DVRs accept BNC analog
 * channels (HDTVI / AHD / CVI / CVBS) AND IP overlay channels on the
 * same chassis.
 *
 * Spec extraction methodology — `pdftotext` (poppler) over the official
 * PDFs, with the resulting plain text in /tmp/dvr-*.txt; every numeric
 * value below is copied straight out of those text files. Fields are
 * left `undefined` whenever the source does not list a value — nothing
 * is guessed.
 *
 * Datasheets fetched 2026-05-14.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const HIKVISION_DVR_TURBO: EquipmentCatalogEntry[] = [
  // ───────────────────────────────────────────────────────────────────
  // Value Series — DS-7100/7200 HGHI-K1 (1080p Lite, 2 MP analog ceiling)
  // ───────────────────────────────────────────────────────────────────

  // Source: assets.hikvision.com (Hikvision CDN)
  // https://assets.hikvision.com/prd/public/all/doc/m000018278/Datasheet-of_DS-7104HGHI-K1_V4.70.160_20230822.pdf
  {
    id: 'hikvision-ds-7104hghi-k1',
    manufacturer: 'Hikvision',
    model: 'DS-7104HGHI-K1',
    category: 'nvr-recorder',
    tagline: '4-ch HDTVI Mini 1U DVR, +1 IP overlay (max 5 total), 1 HDD up to 4 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000018278/Datasheet-of_DS-7104HGHI-K1_V4.70.160_20230822.pdf',
    specs: {
      channels: 4,
      maxResolution: '1080p Lite',
      hddBays: 1,
      hddBayMaxTb: 4,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U', // datasheet calls it "Mini 1U"
      hybrid: true,
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com
  // https://assets.hikvision.com/prd/public/all/doc/m000018279/Datasheet-of_DS-7108HGHI-K1_V4.70.160_20230822.pdf
  {
    id: 'hikvision-ds-7108hghi-k1',
    manufacturer: 'Hikvision',
    model: 'DS-7108HGHI-K1',
    category: 'nvr-recorder',
    tagline: '8-ch HDTVI Mini 1U DVR, +2 IP overlay (max 10 total), 1 HDD up to 4 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000018279/Datasheet-of_DS-7108HGHI-K1_V4.70.160_20230822.pdf',
    specs: {
      channels: 8,
      maxResolution: '1080p Lite',
      hddBays: 1,
      hddBayMaxTb: 4,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U', // "Mini 1U"
      hybrid: true,
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com
  // https://assets.hikvision.com/prd/normal/all/doc/m000018276/Datasheet-of_DS-7204HGHI-K1_V4.70.160_20230822.pdf
  {
    id: 'hikvision-ds-7204hghi-k1',
    manufacturer: 'Hikvision',
    model: 'DS-7204HGHI-K1',
    category: 'nvr-recorder',
    tagline: '4-ch HDTVI 1U DVR, +1 IP overlay (max 5 total), 1 HDD up to 4 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/normal/all/doc/m000018276/Datasheet-of_DS-7204HGHI-K1_V4.70.160_20230822.pdf',
    specs: {
      channels: 4,
      maxResolution: '1080p Lite',
      hddBays: 1,
      hddBayMaxTb: 4,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com
  // https://assets.hikvision.com/prd/public/all/doc/m000018277/Datasheet-of_DS-7208HGHI-K1_V4.70.160_20230822.pdf
  {
    id: 'hikvision-ds-7208hghi-k1',
    manufacturer: 'Hikvision',
    model: 'DS-7208HGHI-K1',
    category: 'nvr-recorder',
    tagline: '8-ch HDTVI 1U DVR, +2 IP overlay (max 10 total), 1 HDD up to 4 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000018277/Datasheet-of_DS-7208HGHI-K1_V4.70.160_20230822.pdf',
    specs: {
      channels: 8,
      maxResolution: '1080p Lite',
      hddBays: 1,
      hddBayMaxTb: 4,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com
  // https://assets.hikvision.com/prd/public/all/doc/m000004903/Datasheet-of_DS-7216HGHI-K1_V4.70.160_20230822.pdf
  {
    id: 'hikvision-ds-7216hghi-k1',
    manufacturer: 'Hikvision',
    model: 'DS-7216HGHI-K1',
    category: 'nvr-recorder',
    tagline: '16-ch HDTVI 1U DVR, +2 IP overlay (max 18 total), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000004903/Datasheet-of_DS-7216HGHI-K1_V4.70.160_20230822.pdf',
    specs: {
      channels: 16,
      maxResolution: '1080p Lite',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com
  // https://assets.hikvision.com/prd/public/all/doc/m000058569/Datasheet-of_DS-7116HGHI-M1_V4.70.160_20230822.pdf
  {
    id: 'hikvision-ds-7116hghi-m1',
    manufacturer: 'Hikvision',
    model: 'DS-7116HGHI-M1',
    category: 'nvr-recorder',
    tagline: '16-ch HDTVI Mini 1U DVR, +2 IP overlay (max 18 total), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000058569/Datasheet-of_DS-7116HGHI-M1_V4.70.160_20230822.pdf',
    specs: {
      channels: 16,
      maxResolution: '1080p Lite',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U', // "Mini 1U"
      hybrid: true,
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // ───────────────────────────────────────────────────────────────────
  // Pro Series — DS-7100/7200 HQHI-K1 (4 MP Lite / 3 MP analog ceiling)
  // ───────────────────────────────────────────────────────────────────

  // Source: assets.hikvision.com — datasheet describes a kit; DVR
  // section lists the DS-7104HQHI-K1 standalone specs verbatim.
  // https://assets.hikvision.com/prd/public/all/doc/m000001883/Datasheet-of-DS-7104HQHI-K1_4CAM_20241212.pdf
  {
    id: 'hikvision-ds-7104hqhi-k1',
    manufacturer: 'Hikvision',
    model: 'DS-7104HQHI-K1',
    category: 'nvr-recorder',
    tagline: '4-ch HDTVI Mini 1U DVR, +2 IP overlay (max 6 total), 1 HDD up to 6 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000001883/Datasheet-of-DS-7104HQHI-K1_4CAM_20241212.pdf',
    specs: {
      channels: 4,
      maxResolution: '4 MP Lite',
      hddBays: 1,
      hddBayMaxTb: 6,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U', // "Mini 1U"
      hybrid: true,
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com
  // https://assets.hikvision.com/prd/public/all/doc/m000005678/Datasheet-of-DS-7116HQHI-K1_V4.70.140_20230621.pdf
  {
    id: 'hikvision-ds-7116hqhi-k1',
    manufacturer: 'Hikvision',
    model: 'DS-7116HQHI-K1',
    category: 'nvr-recorder',
    tagline: '16-ch HDTVI 1U DVR, +8 IP overlay (max 24 total), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000005678/Datasheet-of-DS-7116HQHI-K1_V4.70.140_20230621.pdf',
    specs: {
      channels: 16,
      maxResolution: '4 MP Lite',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com
  // https://assets.hikvision.com/prd/normal/all/doc/m000007622/Datasheet-of-DS-7216HQHI-K1_E_V4.70.140_20230621.pdf
  {
    id: 'hikvision-ds-7216hqhi-k1-e',
    manufacturer: 'Hikvision',
    model: 'DS-7216HQHI-K1/E',
    category: 'nvr-recorder',
    tagline: '16-ch HDTVI 1U DVR, +8 IP overlay (max 24 total), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/normal/all/doc/m000007622/Datasheet-of-DS-7216HQHI-K1_E_V4.70.140_20230621.pdf',
    specs: {
      channels: 16,
      maxResolution: '4 MP Lite',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // ───────────────────────────────────────────────────────────────────
  // Pro Series — DS-7200 HUHI-K1 / K1/E (Turbo HD 5.0, 8 MP @ ch1)
  // ───────────────────────────────────────────────────────────────────

  // Source: gzhls (distributor mirror of Hikvision PDF — official
  // assets.hikvision.com URL is gated behind a region check).
  // https://gzhls.at/blob/ldb/e/8/6/d/3eef37c1d477b416c18fe67caff8e22510d4.pdf
  // ("Datasheet of DS-7200HUHI-K1 series" — covers 7204 + 7208.)
  {
    id: 'hikvision-ds-7204huhi-k1',
    manufacturer: 'Hikvision',
    model: 'DS-7204HUHI-K1',
    category: 'nvr-recorder',
    tagline:
      '4-ch HDTVI 1U DVR (8 MP @ ch1, 5 MP @ all), +2 IP overlay (max 6 total), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://gzhls.at/blob/ldb/e/8/6/d/3eef37c1d477b416c18fe67caff8e22510d4.pdf',
    specs: {
      channels: 4,
      maxResolution: '8 MP', // 8 MP@8fps only on channel 1; 5 MP on all
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      alarmIO: '4 in / 1 out',
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // Source: same DS-7200HUHI-K1 series datasheet (distributor mirror).
  // https://gzhls.at/blob/ldb/e/8/6/d/3eef37c1d477b416c18fe67caff8e22510d4.pdf
  {
    id: 'hikvision-ds-7208huhi-k1',
    manufacturer: 'Hikvision',
    model: 'DS-7208HUHI-K1',
    category: 'nvr-recorder',
    tagline:
      '8-ch HDTVI 1U DVR (5 MP), +4 IP overlay (max 12 total), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://gzhls.at/blob/ldb/e/8/6/d/3eef37c1d477b416c18fe67caff8e22510d4.pdf',
    specs: {
      channels: 8,
      maxResolution: '5 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      alarmIO: '8 in / 4 out',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // Source: SecuritySourcing distributor mirror of Hikvision PDF for the
  // DS-7200HUHI-K1/E series ("/E" = no alarm I/O, no audio in).
  // https://www.securitysourcing.net/src/pdf/DS-7208HUHI-K1-E.pdf
  {
    id: 'hikvision-ds-7208huhi-k1-e',
    manufacturer: 'Hikvision',
    model: 'DS-7208HUHI-K1/E',
    category: 'nvr-recorder',
    tagline:
      '8-ch HDTVI 1U DVR (5 MP), +4 IP overlay (max 12 total), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://www.securitysourcing.net/src/pdf/DS-7208HUHI-K1-E.pdf',
    specs: {
      channels: 8,
      maxResolution: '5 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      // /E variant has no alarm in/out per datasheet.
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // ───────────────────────────────────────────────────────────────────
  // Pro Series PoC — DS-7200 HUHI-K/P (power-over-coax to cameras)
  // ───────────────────────────────────────────────────────────────────

  // Source: SourceSecurity mirror of the Hikvision DS-7200HUHI-K/P
  // series datasheet ("UD06123B"). Covers DS-7204HUHI-K1/P + 7208K2/P.
  // https://www.sourcesecurity.com/datasheets/hikvision-ds-7208huhi-k2-p-digital-video-recorder-dvr/co-3425-ga/11203_DDVRspecKseriesUD06123BDatasheetofDS7200HUHIKPV3.5.2020170623.pdf
  {
    id: 'hikvision-ds-7204huhi-k1-p',
    manufacturer: 'Hikvision',
    model: 'DS-7204HUHI-K1/P',
    category: 'nvr-recorder',
    tagline:
      '4-ch HDTVI PoC 1U DVR (8 MP @ ch1, 5 MP @ all), +2 IP overlay (max 6 total), 1 HDD up to 8 TB',
    verified: true,
    datasheetUrl:
      'https://www.sourcesecurity.com/datasheets/hikvision-ds-7208huhi-k2-p-digital-video-recorder-dvr/co-3425-ga/11203_DDVRspecKseriesUD06123BDatasheetofDS7200HUHIKPV3.5.2020170623.pdf',
    specs: {
      channels: 4,
      maxResolution: '8 MP', // 8 MP@8fps only on channel 1
      hddBays: 1,
      hddBayMaxTb: 8,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      alarmIO: '4 in / 1 out',
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // Source: same DS-7200HUHI-K/P series datasheet (SourceSecurity).
  // https://www.sourcesecurity.com/datasheets/hikvision-ds-7208huhi-k2-p-digital-video-recorder-dvr/co-3425-ga/11203_DDVRspecKseriesUD06123BDatasheetofDS7200HUHIKPV3.5.2020170623.pdf
  {
    id: 'hikvision-ds-7208huhi-k2-p',
    manufacturer: 'Hikvision',
    model: 'DS-7208HUHI-K2/P',
    category: 'nvr-recorder',
    tagline:
      '8-ch HDTVI PoC 1U DVR (5 MP), +8 IP overlay (max 16 total), 2 HDD up to 8 TB each',
    verified: true,
    datasheetUrl:
      'https://www.sourcesecurity.com/datasheets/hikvision-ds-7208huhi-k2-p-digital-video-recorder-dvr/co-3425-ga/11203_DDVRspecKseriesUD06123BDatasheetofDS7200HUHIKPV3.5.2020170623.pdf',
    specs: {
      channels: 8,
      maxResolution: '5 MP',
      hddBays: 2,
      hddBayMaxTb: 8,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      alarmIO: '8 in / 4 out',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // Source: SecuritySourcing distributor mirror of the Hikvision
  // DS-7200HUHI-K/P series datasheet (this revision adds 7216K2/P).
  // https://www.securitysourcing.net/src/pdf/DS-7216HUHI-K2.pdf
  {
    id: 'hikvision-ds-7216huhi-k2-p',
    manufacturer: 'Hikvision',
    model: 'DS-7216HUHI-K2/P',
    category: 'nvr-recorder',
    tagline:
      '16-ch HDTVI PoC 1U DVR (5 MP), +16 IP overlay (max 32 total), 2 HDD up to 10 TB each',
    verified: true,
    datasheetUrl:
      'https://www.securitysourcing.net/src/pdf/DS-7216HUHI-K2.pdf',
    specs: {
      channels: 16,
      maxResolution: '5 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      alarmIO: '4 in / 1 out',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // ───────────────────────────────────────────────────────────────────
  // Pro Series — DS-7300 HQHI-K4 / HUHI-K4 (1.5U, 4 SATA bays)
  // ───────────────────────────────────────────────────────────────────

  // Source: discomp.cz mirror of the Hikvision DS-7300HQHI-K4 series
  // datasheet (assets.hikvision.com URL is region-gated). Covers
  // 7304/7308/7316HQHI-K4 — 4 MP Lite / 6 MP IP ceiling.
  // https://download.discomp.cz/hikvision/datasheets/DS-7300HQHI-K4.pdf
  {
    id: 'hikvision-ds-7304hqhi-k4',
    manufacturer: 'Hikvision',
    model: 'DS-7304HQHI-K4',
    category: 'nvr-recorder',
    tagline:
      '4-ch HDTVI 1.5U DVR, +2 IP overlay (max 6 total), 4 HDD bays up to 10 TB each',
    verified: true,
    datasheetUrl:
      'https://download.discomp.cz/hikvision/datasheets/DS-7300HQHI-K4.pdf',
    specs: {
      channels: 4,
      maxResolution: '4 MP Lite',
      hddBays: 4,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U', // datasheet "1.5U"; nearest typed value
      hybrid: true,
      alarmIO: '16 in / 4 out',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // Source: same DS-7300HQHI-K4 series datasheet (discomp.cz mirror).
  // https://download.discomp.cz/hikvision/datasheets/DS-7300HQHI-K4.pdf
  {
    id: 'hikvision-ds-7308hqhi-k4',
    manufacturer: 'Hikvision',
    model: 'DS-7308HQHI-K4',
    category: 'nvr-recorder',
    tagline:
      '8-ch HDTVI 1.5U DVR, +4 IP overlay (max 12 total), 4 HDD bays up to 10 TB each',
    verified: true,
    datasheetUrl:
      'https://download.discomp.cz/hikvision/datasheets/DS-7300HQHI-K4.pdf',
    specs: {
      channels: 8,
      maxResolution: '4 MP Lite',
      hddBays: 4,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U', // "1.5U"
      hybrid: true,
      alarmIO: '16 in / 4 out',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // Source: same DS-7300HQHI-K4 series datasheet (discomp.cz mirror).
  // https://download.discomp.cz/hikvision/datasheets/DS-7300HQHI-K4.pdf
  {
    id: 'hikvision-ds-7316hqhi-k4',
    manufacturer: 'Hikvision',
    model: 'DS-7316HQHI-K4',
    category: 'nvr-recorder',
    tagline:
      '16-ch HDTVI 1.5U DVR, +8 IP overlay (max 24 total), 4 HDD bays up to 10 TB each',
    verified: true,
    datasheetUrl:
      'https://download.discomp.cz/hikvision/datasheets/DS-7300HQHI-K4.pdf',
    specs: {
      channels: 16,
      maxResolution: '4 MP Lite',
      hddBays: 4,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U', // "1.5U"
      hybrid: true,
      alarmIO: '16 in / 4 out',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // Source: discomp.cz mirror of the Hikvision DS-7300HUHI-K4 series
  // datasheet. Covers 7304/7308/7316HUHI-K4 — Turbo HD 4.0 / 5 MP / 8 MP IP.
  // https://download.discomp.cz/hikvision/datasheets/DS-7300HUHI-K4.pdf
  {
    id: 'hikvision-ds-7304huhi-k4',
    manufacturer: 'Hikvision',
    model: 'DS-7304HUHI-K4',
    category: 'nvr-recorder',
    tagline:
      '4-ch HDTVI 1.5U DVR (5 MP), +2 IP overlay (max 6 total), 4 HDD bays up to 8 TB each',
    verified: true,
    datasheetUrl:
      'https://download.discomp.cz/hikvision/datasheets/DS-7300HUHI-K4.pdf',
    specs: {
      channels: 4,
      maxResolution: '5 MP',
      hddBays: 4,
      hddBayMaxTb: 8,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U', // "1.5U"
      hybrid: true,
      alarmIO: '16 in / 4 out',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // Source: same DS-7300HUHI-K4 series datasheet (discomp.cz mirror).
  // https://download.discomp.cz/hikvision/datasheets/DS-7300HUHI-K4.pdf
  {
    id: 'hikvision-ds-7308huhi-k4',
    manufacturer: 'Hikvision',
    model: 'DS-7308HUHI-K4',
    category: 'nvr-recorder',
    tagline:
      '8-ch HDTVI 1.5U DVR (5 MP), +2 IP overlay (max 10 total), 4 HDD bays up to 8 TB each',
    verified: true,
    datasheetUrl:
      'https://download.discomp.cz/hikvision/datasheets/DS-7300HUHI-K4.pdf',
    specs: {
      channels: 8,
      maxResolution: '5 MP',
      hddBays: 4,
      hddBayMaxTb: 8,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U', // "1.5U"
      hybrid: true,
      alarmIO: '16 in / 4 out',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // Source: same DS-7300HUHI-K4 series datasheet (discomp.cz mirror).
  // https://download.discomp.cz/hikvision/datasheets/DS-7300HUHI-K4.pdf
  {
    id: 'hikvision-ds-7316huhi-k4',
    manufacturer: 'Hikvision',
    model: 'DS-7316HUHI-K4',
    category: 'nvr-recorder',
    tagline:
      '16-ch HDTVI 1.5U DVR (5 MP), +2 IP overlay (max 18 total), 4 HDD bays up to 8 TB each',
    verified: true,
    datasheetUrl:
      'https://download.discomp.cz/hikvision/datasheets/DS-7300HUHI-K4.pdf',
    specs: {
      channels: 16,
      maxResolution: '5 MP',
      hddBays: 4,
      hddBayMaxTb: 8,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U', // "1.5U"
      hybrid: true,
      alarmIO: '16 in / 4 out',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // ───────────────────────────────────────────────────────────────────
  // AcuSense M-series — iDS-7100 / iDS-7200 HQHI-M1 (1080p AcuSense)
  // ───────────────────────────────────────────────────────────────────

  // Source: assets.hikvision.com
  // https://assets.hikvision.com/prd/public/all/doc/m000060309/Datasheet-of-iDS-7104HQHI-M1_S_V4.71.000_20231218.pdf
  {
    id: 'hikvision-ids-7104hqhi-m1-s',
    manufacturer: 'Hikvision',
    model: 'iDS-7104HQHI-M1/S',
    category: 'nvr-recorder',
    tagline:
      '4-ch HDTVI Mini 1U AcuSense DVR (1080p), +2 IP overlay (max 6 total), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000060309/Datasheet-of-iDS-7104HQHI-M1_S_V4.71.000_20231218.pdf',
    specs: {
      channels: 4,
      maxResolution: '1080p',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U', // "Mini 1U"
      hybrid: true,
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com
  // https://assets.hikvision.com/prd/public/all/doc/m000060310/Datasheet-of-iDS-7108HQHI-M1_S_V4.70.180_20231218.pdf
  {
    id: 'hikvision-ids-7108hqhi-m1-s',
    manufacturer: 'Hikvision',
    model: 'iDS-7108HQHI-M1/S',
    category: 'nvr-recorder',
    tagline:
      '8-ch HDTVI Mini 1U AcuSense DVR (1080p), +4 IP overlay (max 12 total), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000060310/Datasheet-of-iDS-7108HQHI-M1_S_V4.70.180_20231218.pdf',
    specs: {
      channels: 8,
      maxResolution: '1080p',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U', // "Mini 1U"
      hybrid: true,
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com
  // https://assets.hikvision.com/prd/public/all/doc/m000058561/Datasheet-of-iDS-7116HQHI-M1_S_V4.71.000_20231218.pdf
  {
    id: 'hikvision-ids-7116hqhi-m1-s',
    manufacturer: 'Hikvision',
    model: 'iDS-7116HQHI-M1/S',
    category: 'nvr-recorder',
    tagline:
      '16-ch HDTVI Mini 1U AcuSense DVR (1080p), +8 IP overlay (max 24 total), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000058561/Datasheet-of-iDS-7116HQHI-M1_S_V4.71.000_20231218.pdf',
    specs: {
      channels: 16,
      maxResolution: '1080p',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U', // "Mini 1U"
      hybrid: true,
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com
  // https://assets.hikvision.com/prd/public/all/doc/m000007976/Datasheet-of-iDS-7204HQHI-M1_S_V4.71.000_20240110.pdf
  {
    id: 'hikvision-ids-7204hqhi-m1-s',
    manufacturer: 'Hikvision',
    model: 'iDS-7204HQHI-M1/S',
    category: 'nvr-recorder',
    tagline:
      '4-ch HDTVI 1U AcuSense DVR (1080p), +2 IP overlay (max 6 total), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000007976/Datasheet-of-iDS-7204HQHI-M1_S_V4.71.000_20240110.pdf',
    specs: {
      channels: 4,
      maxResolution: '1080p',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com
  // https://assets.hikvision.com/prd/public/all/doc/m000007977/Datasheet-of-iDS-7208HQHI-M1_S_V4.71.000_20230621.pdf
  {
    id: 'hikvision-ids-7208hqhi-m1-s',
    manufacturer: 'Hikvision',
    model: 'iDS-7208HQHI-M1/S',
    category: 'nvr-recorder',
    tagline:
      '8-ch HDTVI 1U AcuSense DVR (1080p), +4 IP overlay (max 12 total), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000007977/Datasheet-of-iDS-7208HQHI-M1_S_V4.71.000_20230621.pdf',
    specs: {
      channels: 8,
      maxResolution: '1080p',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com
  // https://assets.hikvision.com/prd/normal/all/doc/m000007979/Datasheet-of-iDS-7216HQHI-M1_S_V4.71.000_20240122.pdf
  {
    id: 'hikvision-ids-7216hqhi-m1-s',
    manufacturer: 'Hikvision',
    model: 'iDS-7216HQHI-M1/S',
    category: 'nvr-recorder',
    tagline:
      '16-ch HDTVI 1U AcuSense DVR (1080p), +8 IP overlay (max 24 total), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/normal/all/doc/m000007979/Datasheet-of-iDS-7216HQHI-M1_S_V4.71.000_20240122.pdf',
    specs: {
      channels: 16,
      maxResolution: '1080p',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com — /E variant (no audio in, no alarm).
  // https://assets.hikvision.com/prd/public/all/doc/m000060308/Datasheet-of-iDS-7208HQHI-M1_E_V4.71.000_20231218.pdf
  {
    id: 'hikvision-ids-7208hqhi-m1-e',
    manufacturer: 'Hikvision',
    model: 'iDS-7208HQHI-M1/E',
    category: 'nvr-recorder',
    tagline:
      '8-ch HDTVI 1U AcuSense DVR (1080p), +4 IP overlay (max 12 total), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000060308/Datasheet-of-iDS-7208HQHI-M1_E_V4.71.000_20231218.pdf',
    specs: {
      channels: 8,
      maxResolution: '1080p',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com — /XT export variant (later revision,
  // same 8-ch 1080p AcuSense platform).
  // https://assets.hikvision.com/prd/public/all/doc/m000128024/iDS-7208HQHI-M1_XT_Datasheet_20241018.pdf
  {
    id: 'hikvision-ids-7208hqhi-m1-xt',
    manufacturer: 'Hikvision',
    model: 'iDS-7208HQHI-M1/XT',
    category: 'nvr-recorder',
    tagline:
      '8-ch HDTVI 1U AcuSense DVR (1080p), +4 IP overlay (max 12 total), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000128024/iDS-7208HQHI-M1_XT_Datasheet_20241018.pdf',
    specs: {
      channels: 8,
      maxResolution: '1080p',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com — /XT variant.
  // https://assets.hikvision.com/prd/public/all/doc/m000128025/iDS-7204HQHI-M1_XT_Datasheet_20250208.pdf
  {
    id: 'hikvision-ids-7204hqhi-m1-xt',
    manufacturer: 'Hikvision',
    model: 'iDS-7204HQHI-M1/XT',
    category: 'nvr-recorder',
    tagline:
      '4-ch HDTVI 1U AcuSense DVR (1080p), +2 IP overlay (max 6 total), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000128025/iDS-7204HQHI-M1_XT_Datasheet_20250208.pdf',
    specs: {
      channels: 4,
      maxResolution: '1080p',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com — /XT variant.
  // https://assets.hikvision.com/prd/public/all/doc/m000120171/Datasheet-of-iDS-7216HQHI-M1_XT_V4.71.500_20240311.pdf
  {
    id: 'hikvision-ids-7216hqhi-m1-xt',
    manufacturer: 'Hikvision',
    model: 'iDS-7216HQHI-M1/XT',
    category: 'nvr-recorder',
    tagline:
      '16-ch HDTVI 1U AcuSense DVR (1080p), +8 IP overlay (max 24 total), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000120171/Datasheet-of-iDS-7216HQHI-M1_XT_V4.71.500_20240311.pdf',
    specs: {
      channels: 16,
      maxResolution: '1080p',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // ───────────────────────────────────────────────────────────────────
  // AcuSense M-series — iDS-7100 / iDS-7200 HUHI-M1 / M2 (5 MP / 8 MP)
  // ───────────────────────────────────────────────────────────────────

  // Source: assets.hikvision.com — Mini 1U 5 MP AcuSense with Wi-Fi dongle.
  // https://assets.hikvision.com/prd/public/all/doc/m000060311/Datasheet-of-iDS-7104HUHI-M1_S_20241212.pdf
  {
    id: 'hikvision-ids-7104huhi-m1-s',
    manufacturer: 'Hikvision',
    model: 'iDS-7104HUHI-M1/S',
    category: 'nvr-recorder',
    tagline:
      '4-ch HDTVI Mini 1U AcuSense DVR (5 MP, 8 MP @ ch1), +2 IP overlay (max 6 total), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000060311/Datasheet-of-iDS-7104HUHI-M1_S_20241212.pdf',
    specs: {
      channels: 4,
      maxResolution: '8 MP', // 8 MP@8fps on ch1; 5 MP @ all
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U', // "Mini 1U"
      hybrid: true,
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com — confirmed via m000007981 with the
  // assets.hikvision.com filename pattern.
  // https://assets.hikvision.com/prd/public/all/doc/m000007981/Datasheet-of-iDS-7204HUHI-M1_S_V4.71.000_20230621.pdf
  {
    id: 'hikvision-ids-7204huhi-m1-s',
    manufacturer: 'Hikvision',
    model: 'iDS-7204HUHI-M1/S',
    category: 'nvr-recorder',
    tagline:
      '4-ch HDTVI 1U AcuSense DVR (5 MP, 8 MP @ ch1), +2 IP overlay (max 6 total), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000007981/Datasheet-of-iDS-7204HUHI-M1_S_V4.71.000_20230621.pdf',
    specs: {
      channels: 4,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com
  // https://assets.hikvision.com/prd/normal/all/doc/m000007983/Datasheet-of-iDS-7208HUHI-M1_S_V4.71.000_20231218.pdf
  {
    id: 'hikvision-ids-7208huhi-m1-s',
    manufacturer: 'Hikvision',
    model: 'iDS-7208HUHI-M1/S',
    category: 'nvr-recorder',
    tagline:
      '8-ch HDTVI 1U AcuSense DVR (5 MP, 8 MP @ ch1), +8 IP overlay (max 16 total), 1 HDD up to 12 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/normal/all/doc/m000007983/Datasheet-of-iDS-7208HUHI-M1_S_V4.71.000_20231218.pdf',
    specs: {
      channels: 8,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 12,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com — /E variant.
  // https://assets.hikvision.com/prd/public/all/doc/m000060306/Datasheet-of-iDS-7204HUHI-M1_E_V4.71.000_20230621.pdf
  {
    id: 'hikvision-ids-7204huhi-m1-e',
    manufacturer: 'Hikvision',
    model: 'iDS-7204HUHI-M1/E',
    category: 'nvr-recorder',
    tagline:
      '4-ch HDTVI 1U AcuSense DVR (5 MP), +2 IP overlay (max 6 total), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000060306/Datasheet-of-iDS-7204HUHI-M1_E_V4.71.000_20230621.pdf',
    specs: {
      channels: 4,
      maxResolution: '5 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com — /E variant.
  // https://assets.hikvision.com/prd/public/all/doc/m000058564/Datasheet-of-iDS-7208HUHI-M1_E_V4.71.000_20230621.pdf
  {
    id: 'hikvision-ids-7208huhi-m1-e',
    manufacturer: 'Hikvision',
    model: 'iDS-7208HUHI-M1/E',
    category: 'nvr-recorder',
    tagline:
      '8-ch HDTVI 1U AcuSense DVR (5 MP), +4 IP overlay (max 12 total), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000058564/Datasheet-of-iDS-7208HUHI-M1_E_V4.71.000_20230621.pdf',
    specs: {
      channels: 8,
      maxResolution: '5 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com — /FA face-detection variant.
  // https://assets.hikvision.com/prd/public/all/doc/m000018777/Datasheet-of-iDS-7204HUHI-M1_FA_V4.71.000_20230210.pdf
  {
    id: 'hikvision-ids-7204huhi-m1-fa',
    manufacturer: 'Hikvision',
    model: 'iDS-7204HUHI-M1/FA',
    category: 'nvr-recorder',
    tagline:
      '4-ch HDTVI 1U AcuSense DVR with face capture (5 MP), +2 IP overlay, 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000018777/Datasheet-of-iDS-7204HUHI-M1_FA_V4.71.000_20230210.pdf',
    specs: {
      channels: 4,
      maxResolution: '5 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com — /FA face-detection variant.
  // https://assets.hikvision.com/prd/public/all/doc/m000018391/Datasheet-of-iDS-7208HUHI-M1_FA_V4.71.140_20230210.pdf
  {
    id: 'hikvision-ids-7208huhi-m1-fa',
    manufacturer: 'Hikvision',
    model: 'iDS-7208HUHI-M1/FA',
    category: 'nvr-recorder',
    tagline:
      '8-ch HDTVI 1U AcuSense DVR with face capture (5 MP), +4 IP overlay, 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000018391/Datasheet-of-iDS-7208HUHI-M1_FA_V4.71.140_20230210.pdf',
    specs: {
      channels: 8,
      maxResolution: '5 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com — /X late-revision variant with face
  // recognition and 24-ch decoding.
  // https://assets.hikvision.com/prd/public/all/doc/m000128020/iDS-7204HUHI-M1_X_Datasheet_20241018.pdf
  {
    id: 'hikvision-ids-7204huhi-m1-x',
    manufacturer: 'Hikvision',
    model: 'iDS-7204HUHI-M1/X',
    category: 'nvr-recorder',
    tagline:
      '4-ch HDTVI 1U AcuSense DVR (8 MP @ ch1, 5 MP @ all), +4 IP overlay (max 8 total), 1 HDD up to 12 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000128020/iDS-7204HUHI-M1_X_Datasheet_20241018.pdf',
    specs: {
      channels: 4,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 12,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com — /X late-revision variant.
  // https://assets.hikvision.com/prd/public/all/doc/m000120174/iDS-7208HUHI-M1_X_Datasheet_20241018.pdf
  {
    id: 'hikvision-ids-7208huhi-m1-x',
    manufacturer: 'Hikvision',
    model: 'iDS-7208HUHI-M1/X',
    category: 'nvr-recorder',
    tagline:
      '8-ch HDTVI 1U AcuSense DVR (8 MP @ ch1, 5 MP @ all), +8 IP overlay (max 16 total), 1 HDD up to 12 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000120174/iDS-7208HUHI-M1_X_Datasheet_20241018.pdf',
    specs: {
      channels: 8,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 12,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com — M2 (dual-bay) variant.
  // https://assets.hikvision.com/prd/public/all/doc/m000007984/Datasheet-of-iDS-7208HUHI-M2_S_V4.71.000_20230621.pdf
  {
    id: 'hikvision-ids-7208huhi-m2-s',
    manufacturer: 'Hikvision',
    model: 'iDS-7208HUHI-M2/S',
    category: 'nvr-recorder',
    tagline:
      '8-ch HDTVI 1U AcuSense DVR (5 MP, 8 MP @ ch1), +8 IP overlay (max 16 total), 2 HDD up to 10 TB each',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000007984/Datasheet-of-iDS-7208HUHI-M2_S_V4.71.000_20230621.pdf',
    specs: {
      channels: 8,
      maxResolution: '8 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com — M2 dual-bay variant.
  // https://assets.hikvision.com/prd/public/all/doc/m000033682/Datasheet-of-iDS-7216HUHI-M2_S_V4.71.000_20230621.pdf
  {
    id: 'hikvision-ids-7216huhi-m2-s',
    manufacturer: 'Hikvision',
    model: 'iDS-7216HUHI-M2/S',
    category: 'nvr-recorder',
    tagline:
      '16-ch HDTVI 1U AcuSense DVR (5 MP, 8 MP @ ch1), +8 IP overlay (max 24 total), 2 HDD up to 10 TB each',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000033682/Datasheet-of-iDS-7216HUHI-M2_S_V4.71.000_20230621.pdf',
    specs: {
      channels: 16,
      maxResolution: '8 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // ───────────────────────────────────────────────────────────────────
  // AcuSense Pro Series — iDS-7300 HUHI-M4 (1.5U, 4 SATA bays)
  // ───────────────────────────────────────────────────────────────────

  // Source: assets.hikvision.com
  // https://assets.hikvision.com/prd/public/all/doc/m000065333/Datasheet-of-iDS-7316HUHI-M4_20241212.pdf
  {
    id: 'hikvision-ids-7316huhi-m4-s',
    manufacturer: 'Hikvision',
    model: 'iDS-7316HUHI-M4/S',
    category: 'nvr-recorder',
    tagline:
      '16-ch HDTVI 1.5U AcuSense DVR (5 MP), +2 IP overlay (max 18 total), 4 HDD bays up to 12 TB each',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000065333/Datasheet-of-iDS-7316HUHI-M4_20241212.pdf',
    specs: {
      channels: 16,
      maxResolution: '5 MP',
      hddBays: 4,
      hddBayMaxTb: 12,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U', // "1.5U"
      hybrid: true,
      alarmIO: '16 in / 4 out',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // Source: assets.hikvision.com
  // https://assets.hikvision.com/prd/public/all/doc/m000065334/Datasheet-of-iDS-7332HUHI-M4_S_V4.5.0.200_20220901.pdf
  {
    id: 'hikvision-ids-7332huhi-m4-s',
    manufacturer: 'Hikvision',
    model: 'iDS-7332HUHI-M4/S',
    category: 'nvr-recorder',
    tagline:
      '32-ch HDTVI 1.5U AcuSense DVR (5 MP), +2 IP overlay (max 34 total), 4 HDD bays up to 12 TB each',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000065334/Datasheet-of-iDS-7332HUHI-M4_S_V4.5.0.200_20220901.pdf',
    specs: {
      channels: 32,
      maxResolution: '5 MP',
      hddBays: 4,
      hddBayMaxTb: 12,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U', // "1.5U"
      hybrid: true,
      alarmIO: '16 in / 4 out',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },
];
